import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiFileText, FiUploadCloud } from 'react-icons/fi';
import { createSystemAgreement, deleteSystemAgreement, getSystemAgreements, uploadSystemAgreementFile } from '../../../api/services/adminService';
import './UserAgreement.css';

const initialForm = {
  title: '',
  version: '',
  summary: '',
  file: null,
};

const UserAgreement = () => {
  const [agreements, setAgreements] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingAgreementId, setDeletingAgreementId] = useState(null);
  const [viewMode, setViewMode] = useState('all');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const normalizeAgreements = (payload) => {
    const list = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.agreements)
        ? payload.agreements
        : Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload?.content)
            ? payload.content
            : [];

    return list.map((item) => ({ ...item }));
  };

  const filteredAgreements = useMemo(() => {
    if (viewMode === 'active') {
      return agreements.filter((item) => item.active || item.isActive || item.isActiveAgreement);
    }
    return agreements;
  }, [agreements, viewMode]);

  const loadAgreements = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const agreementsResponse = await getSystemAgreements();
      const agreementsPayload = agreementsResponse?.data ?? agreementsResponse;
      setAgreements(normalizeAgreements(agreementsPayload));
    } catch (err) {
      setError(err.message || 'Failed to load agreements');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgreements();
  }, [loadAgreements]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setForm((prev) => ({ ...prev, file }));
    setSelectedFileName(file ? file.name : '');
  };

  const handleDeleteAgreement = async (agreement) => {
    if (!agreement?.id) return;
    const confirmed = window.confirm(`Delete agreement "${agreement.title || 'this version'}"? This will remove the version and its acceptances.`);
    if (!confirmed) return;

    setDeletingAgreementId(agreement.id);
    setError('');
    setSuccess('');
    try {
      await deleteSystemAgreement(agreement.id);
      setSuccess('Agreement deleted successfully.');
      await loadAgreements();
    } catch (err) {
      setError(err.message || 'Failed to delete agreement.');
    } finally {
      setDeletingAgreementId(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!form.title.trim()) {
      setError('Please enter a title.');
      return;
    }

    if (!form.version.trim()) {
      setError('Please enter a version number.');
      return;
    }

    if (!form.file) {
      setError('Please upload a PDF file first.');
      return;
    }

    setIsSaving(true);
    try {
      const uploadResponse = await uploadSystemAgreementFile(form.file);
      const uploadedUrl = uploadResponse?.data?.url || uploadResponse?.url || uploadResponse?.data?.agreementUrl || uploadResponse?.agreementUrl;

      if (!uploadedUrl) {
        throw new Error('The file upload did not return a valid URL.');
      }

      await createSystemAgreement({
        title: form.title.trim(),
        agreementUrl: uploadedUrl,
        version: form.version.trim(),
        summary: form.summary.trim() || undefined,
      });

      setSuccess('Agreement uploaded successfully and set as the active version.');
      setForm(initialForm);
      setSelectedFileName('');
      await loadAgreements();
    } catch (err) {
      setError(err.message || 'Failed to upload the agreement.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="user-agreement-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">User Agreement</h1>
          <p className="page-subtitle">
            Upload a new PDF agreement for users. The latest version becomes active and replaces the previous one.
          </p>
        </div>
      </div>

      <div className="user-agreement-grid">
        <form className="user-agreement-card form-card" onSubmit={handleSubmit}>
          <div className="card-head">
            <div className="card-icon">
              <FiUploadCloud />
            </div>
            <div>
              <h2>Upload New Agreement</h2>
              <p>Upload the PDF first, then submit the agreement details.</p>
            </div>
          </div>

          {error ? <div className="form-alert form-alert-error">{error}</div> : null}
          {success ? <div className="form-alert form-alert-success">{success}</div> : null}

          <label className="field-group">
            <span>Title</span>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="TenantScore Terms of Service"
            />
          </label>

          <label className="field-group">
            <span>Version</span>
            <input
              type="text"
              name="version"
              value={form.version}
              onChange={handleChange}
              placeholder="1.0.0"
            />
          </label>

          <label className="field-group">
            <span>Summary</span>
            <textarea
              name="summary"
              value={form.summary}
              onChange={handleChange}
              rows="4"
              placeholder="Please read and accept the platform terms before continuing."
            />
          </label>

          <label className="field-group file-field">
            <span>Agreement PDF</span>
            <input type="file" accept="application/pdf" onChange={handleFileChange} />
            <small>{selectedFileName || 'Choose a PDF file to upload'}</small>
          </label>

          <button className="btn btn-primary submit-btn" type="submit" disabled={isSaving}>
            {isSaving ? 'Uploading…' : 'Upload Agreement'}
          </button>
        </form>

        <div className="user-agreement-card list-card">
          <div className="card-head">
            <div className="card-icon">
              <FiFileText />
            </div>
            <div>
              <h2>Agreements</h2>
              <p>Browse all agreements or focus on the active version.</p>
            </div>
          </div>

          <div className="agreement-view-toggle" role="tablist" aria-label="Agreement view filters">
            <button
              type="button"
              className={`agreement-view-btn ${viewMode === 'all' ? 'active' : ''}`}
              onClick={() => setViewMode('all')}
            >
              All agreements
            </button>
            <button
              type="button"
              className={`agreement-view-btn ${viewMode === 'active' ? 'active' : ''}`}
              onClick={() => setViewMode('active')}
            >
              Active only
            </button>
          </div>

          {isLoading ? (
            <div className="empty-state">Loading agreements…</div>
          ) : filteredAgreements.length === 0 ? (
            <div className="empty-state">No agreements match the selected filter.</div>
          ) : (
            <div className="agreement-list">
              {filteredAgreements.map((agreement) => (
                <div
                  className={`agreement-card ${agreement.active || agreement.isActive || agreement.isActiveAgreement ? 'agreement-card-active' : ''}`}
                  key={agreement.id || agreement.version || agreement.title}
                >
                  <div className="agreement-card-header">
                    <div>
                      <div className="agreement-card-status">
                        {agreement.active || agreement.isActive || agreement.isActiveAgreement ? (
                          <span className="active-pill">Active</span>
                        ) : null}
                        <span className="version-pill">v{agreement.version || '—'}</span>
                      </div>
                      <h3 className="agreement-card-title">{agreement.title || 'Untitled agreement'}</h3>
                    </div>
                    <button
                      type="button"
                      className="agreement-delete-btn"
                      onClick={() => handleDeleteAgreement(agreement)}
                      disabled={deletingAgreementId === agreement.id}
                    >
                      {deletingAgreementId === agreement.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                  <p className="agreement-summary">{agreement.summary || 'No summary provided.'}</p>
                  {agreement.agreementUrl ? (
                    <a href={agreement.agreementUrl} target="_blank" rel="noreferrer" className="agreement-link">
                      View PDF
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAgreement;
